import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Avatar,
  Grid,
  Divider,
  IconButton
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useAuth } from '../contexts/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

const Profile = () => {
  const { currentUser, updateUserProfile, logout, error, setError } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [photoURL, setPhotoURL] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.displayName || '',
        email: currentUser.email || '',
        phone: currentUser.phone || ''
      });
      setPhotoURL(currentUser.photoURL || '');
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setPhotoFile(e.target.files[0]);
      // Create a preview URL
      setPhotoURL(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setMessage('');
      setError('');
      setLoading(true);
      
      let uploadedPhotoURL = currentUser.photoURL;
      
      // Upload new photo if selected
      if (photoFile) {
        const fileRef = ref(storage, `profile-photos/${currentUser.uid}/${photoFile.name}`);
        await uploadBytes(fileRef, photoFile);
        uploadedPhotoURL = await getDownloadURL(fileRef);
      }
      
      // Update user profile
      await updateUserProfile(
        formData.name !== currentUser.displayName ? formData.name : null,
        formData.phone !== currentUser.phone ? formData.phone : null,
        uploadedPhotoURL !== currentUser.photoURL ? uploadedPhotoURL : null
      );
      
      setMessage('Perfil actualizado con éxito');
    } catch (error) {
      console.error('Profile update error:', error);
      setError('Error al actualizar el perfil. Por favor intenta de nuevo');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setError('Error al cerrar sesión. Por favor intenta de nuevo');
    }
  };

  if (!currentUser) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Debes iniciar sesión para ver tu perfil
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{ mt: 2, backgroundColor: '#F2C641', '&:hover': { backgroundColor: '#d9b13b' } }}
          >
            Iniciar Sesión
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography component="h1" variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
            Mi Perfil
          </Typography>
          
          {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
          {message && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{message}</Alert>}
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', mb: 3 }}>
                <Avatar
                  src={photoURL}
                  alt={formData.name}
                  sx={{ width: 150, height: 150, mb: 2 }}
                />
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                  }}
                >
                  <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                  <PhotoCameraIcon />
                </IconButton>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Haz clic en el icono de cámara para cambiar tu foto de perfil
              </Typography>
              
              <Button
                fullWidth
                variant="outlined"
                color="error"
                onClick={handleLogout}
                sx={{ mt: 'auto' }}
              >
                Cerrar Sesión
              </Button>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="name"
                  label="Nombre Completo"
                  name="name"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      '&:hover fieldset': {
                        borderColor: '#F2C641',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#F2C641',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#F2C641',
                    },
                  }}
                />
                
                <TextField
                  margin="normal"
                  fullWidth
                  id="email"
                  label="Correo Electrónico"
                  name="email"
                  value={formData.email}
                  disabled={true}
                  helperText="El correo electrónico no se puede cambiar"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    },
                  }}
                />
                
                <TextField
                  margin="normal"
                  fullWidth
                  id="phone"
                  label="Teléfono"
                  name="phone"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      '&:hover fieldset': {
                        borderColor: '#F2C641',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#F2C641',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#F2C641',
                    },
                  }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{ backgroundColor: '#F2C641', '&:hover': { backgroundColor: '#d9b13b' } }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Guardar Cambios'}
                  </Button>
                </Box>
              </Box>
              
              {currentUser.role === 'provider' && (
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 3 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Cuenta de Proveedor
                  </Typography>
                  <Typography variant="body1">
                    Tienes una cuenta de proveedor. Puedes administrar tus servicios y disponibilidad desde la sección de proveedor.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate('/provider-dashboard')}
                    sx={{ mt: 2 }}
                  >
                    Ir a Panel de Proveedor
                  </Button>
                </Box>
              )}
              
              {currentUser.role !== 'provider' && (
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 3 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    ¿Quieres ofrecer tus servicios?
                  </Typography>
                  <Typography variant="body1">
                    Conviértete en proveedor de servicios en Fixmigo y comienza a recibir solicitudes.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate('/provider-application')}
                    sx={{ mt: 2 }}
                  >
                    Convertirme en Proveedor
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default Profile; 